import React from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';

const TicketTrackingSetup = () => {
  const navigate = useNavigate();

  const setupOptions = [
    {
      id: 'ticket-reports',
      title: 'Drop in Ticket Reports',
      subtitle: '(Agency Upload)',
      description: 'Upload ticket reports directly from your agent or promoter.',
      extendedDescription: 'Supports PDFs and images, making it easy to capture ticket counts, capacity, and updates without reformatting files.',
      icon: (
        <svg className="w-5 h-5 text-[#FF6B35]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      ),
      iconBg: 'rgba(255, 107, 53, 0.15)',
      action: () => navigate('/connectors/data-upload', { state: { type: 'booking' } })
    },
    {
      id: 'auto-flow',
      title: 'Real-Time Sync with Dizee Ticket Tracking Sheets',
      subtitle: '(Auto Flow)',
      description: 'Automatically keep ticket counts up to date using Dizee Sheets.',
      extendedDescription: 'Convert your existing ticket tracking spreadsheet into a Dizee Sheet and continue working in Google Sheets while updates sync into Dizee in real time.',
      icon: (
        <svg className="w-5 h-5 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
        </svg>
      ),
      iconBg: 'rgba(0, 206, 209, 0.15)',
      action: () => navigate('/connectors/google-sheets')
    },
    {
      id: 'manual-add',
      title: 'Manual Add via Dizee AI or Bookings',
      subtitle: '(Quick Updates)',
      description: 'Add or update ticket counts manually when needed.',
      extendedDescription: 'Use the AI Chat to drop in quick ticket counts in natural language or open the Bookings app and fill out the fields directly.',
      icon: (
        <svg className="w-5 h-5 text-[#A78BFA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
        </svg>
      ),
      iconBg: 'rgba(167, 139, 250, 0.15)',
      action: () => navigate('/bookings/ticket-counter')
    }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Live Show Ticket Tracking</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/data')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Page Title */}
            <div className="mb-2">
              <h1 className="text-white text-[20px] font-semibold">Live Show Ticket Tracking</h1>
            </div>

            {/* Description */}
            <div className="mb-8">
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.6]">
                Track ticket sales and performance for every show in one place. Keep real-time visibility into how each date is selling, from early on-sale through final numbers.
              </p>
            </div>

            {/* Setup Options */}
            <div className="space-y-4">
              {setupOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={option.action}
                  className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.05)] cursor-pointer transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border border-[rgba(255,255,255,0.1)]"
                      style={{ backgroundColor: option.iconBg }}
                    >
                      {option.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white text-[13px] font-medium mb-1">
                        {option.title}
                      </h3>
                      {option.subtitle && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                          {option.subtitle}
                        </p>
                      )}
                      <p className="text-[rgba(255,255,255,0.55)] text-[11px] leading-[1.5] mb-2">
                        {option.description}
                      </p>
                      {option.extendedDescription && (
                        <p className="text-[rgba(255,255,255,0.45)] text-[11px] leading-[1.5]">
                          {option.extendedDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TicketTrackingSetup;

